import React from 'react';
import ReactDOM from 'react-dom';
import { motion, AnimatePresence, useReducedMotion, useDragControls } from 'framer-motion';
import { MdUpdate, MdShuffle, MdTrendingUp, MdReplay } from 'react-icons/md';
import { FiX } from 'react-icons/fi';
import { getSheetBackdropMotion, getSheetPanelMotion, useSheetSlideIn } from '../../utils/sheetMotion';

const BENEFITS = [
  { icon: MdUpdate, title: 'Wraca, zanim zapomnisz', method: 'Spaced repetition.', text: 'Pewniaki i niepewniaki wracają dokładnie wtedy, gdy zaczynasz zapominać.' },
  { icon: MdShuffle, title: 'Działy przeplatane jak na maturze', method: 'Interleaving.', text: 'Zadania z różnych działów są mieszane, więc uczysz się rozpoznawać, jaką metodę wybrać.' },
  { icon: MdTrendingUp, title: 'Poziom rośnie razem z Tobą', method: 'Adaptive learning.', text: 'System ELO dobiera zadania trudne, ale zawsze do zrobienia.' },
  { icon: MdReplay, title: 'Po pomyłce od razu podobne zadanie', method: null, text: 'Utrwalasz dobry sposób, zanim błąd zdąży się utrwalić.' },
];

const LearningSystemSheet = ({ isOpen, onClose }) => {
  const prefersReducedMotion = useReducedMotion();
  const slideIn = useSheetSlideIn(isOpen);
  const dragControls = useDragControls();

  return ReactDOM.createPortal(
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            key="learning-system-backdrop"
            {...getSheetBackdropMotion()}
            className="fixed inset-0 bg-black/50 z-[9990]"
            onClick={onClose}
          />
          <motion.div
            key="learning-system-sheet"
            role="dialog"
            aria-modal="true"
            aria-labelledby="learning-system-title"
            {...getSheetPanelMotion(prefersReducedMotion, slideIn)}
            drag="y"
            dragControls={dragControls}
            dragListener={false}
            dragConstraints={{ top: 0 }}
            dragElastic={{ top: 0, bottom: 0.4 }}
            onDragEnd={(_, info) => { if (info.offset.y > 100 || info.velocity.y > 500) onClose(); }}
            className="fixed bottom-0 left-0 right-0 z-[9991] bg-white rounded-t-2xl shadow-[0_-4px_30px_rgba(0,0,0,0.2)] flex flex-col max-h-[92vh] lg:max-w-lg lg:mx-auto lg:bottom-[5vh] lg:rounded-2xl"
          >
            <div
              className="flex justify-center pt-3 pb-1 flex-shrink-0 cursor-grab touch-none"
              onPointerDown={(e) => dragControls.start(e)}
            >
              <div className="w-10 h-1 bg-gray-200 rounded-full" />
            </div>

            <div className="overflow-y-auto px-5 pt-3 pb-[calc(1.25rem+env(safe-area-inset-bottom,0px))]">
              <div className="flex items-start justify-between gap-3">
                <h3 id="learning-system-title" className="font-apotek text-xl font-bold uppercase tracking-wide leading-tight text-[#233855]">
                  Uczysz się krócej, pamiętasz dłużej
                </h3>
                <button
                  type="button"
                  onClick={onClose}
                  className="-mt-1 flex-none p-2 bg-gray-50 rounded-full text-gray-400 hover:text-[#233855] transition-colors"
                  aria-label="Zamknij"
                >
                  <FiX size={20} />
                </button>
              </div>
              <p className="mt-1 mb-5 text-sm text-gray-500">System nauki oparty na badaniach naukowych</p>

              <ul className="grid gap-3">
                {BENEFITS.map(({ icon: Icon, title, method, text }) => (
                  <li key={title} className="rounded-xl border border-gray-200 px-4 py-4">
                    <div className="flex items-center gap-3">
                      <span className="flex-none w-8 h-8 rounded-[9px] bg-[#233855] text-[#F2C46D] grid place-items-center">
                        <Icon className="text-lg" />
                      </span>
                      <p className="text-sm font-semibold text-[#233855]">{title}</p>
                    </div>
                    <p className="mt-2.5 text-[13px] leading-relaxed text-gray-500">
                      {method && <span className="font-semibold text-[#E09F2E]">{method} </span>}
                      {text}
                    </p>
                  </li>
                ))}
              </ul>

              <button
                type="button"
                onClick={onClose}
                className="mt-6 w-full rounded-lg px-4 py-3 font-apotek text-sm font-bold uppercase tracking-widest text-white bg-[#233855] hover:bg-[#345081] transition-colors"
              >
                Rozumiem
              </button>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>,
    document.body
  );
};

export default LearningSystemSheet;
